import { processEvent, getEventTimestamp, unwrapEventData } from './sessionHelper.js';
import { getAlertDetails } from './alertHelper.js';
import { getSessionTypeColor, PASSAGE_COLORS, routePassageColor } from './timelineHelper.js';

const asBoolean = value => value === true || value === 'true';
const stripIcon = text => String(text || '').replace(/^[^\p{L}\p{N}]+/u, '');
const GREY = '#94a3b8';
const CYAN = '#00BCD4';
const RED = '#D32F2F';
const ORANGE = '#FF9800';

const PROPERTY_LABELS = {
    isCorridorEnabled: 'Corridor de sécurité',
    corridorZone: 'Zone du corridor',
    corridorSizeMeters: 'Largeur du corridor',
    isStopDetectionEnabled: 'Détection d’arrêt',
    stopDetectionDurationMs: 'Délai d’arrêt',
    isAccidentDetectionEnabled: 'Détection d’accident',
    isShakeDetectionEnabled: 'Détection de secousse',
    isShortcutDetectionEnabled: 'Raccourci d’alerte',
    shortcutDetectionMode: 'Mode du raccourci',
    selectedTravelMode: 'Mode de déplacement',
    travelMode: 'Mode de déplacement',
    detectionOptions: 'Options de détection',
};

const SPORT_CHECK_REASONS = {
    STOPPED: 'arrêt prolongé',
    OFF_TRAIL: 'hors du parcours',
    RETURN_DEADLINE: 'retour en retard',
};

const formatHour = ms => {
    const d = new Date(Number(ms));
    return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function readData(event) {
    let data = event.eventData ?? event.value ?? event;
    if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch (_) { /* texte brut */ }
    }
    return data && typeof data === 'object' ? data : {};
}

const shortValue = value => {
    const parsed = unwrapEventData(value);
    if (parsed === true || parsed === 'true') return 'activé';
    if (parsed === false || parsed === 'false') return 'désactivé';
    if (parsed === null || parsed === undefined || parsed === '') return '—';
    if (typeof parsed === 'object') return Array.isArray(parsed) ? `${parsed.length} élément(s)` : 'mis à jour';
    return String(parsed).slice(0, 60);
};

/** Destinataires d'un partage : amis prévenus (e-mail, sinon nom), sinon « une personne ». */
function shareRecipients(people) {
    const friends = Object.values(people || {}).filter(person => person?.kind === 'ami')
        .map(person => person.email || person.displayName).filter(Boolean);
    return friends.length ? friends.join(', ') : 'une personne';
}

/** Texte d'une ligne du journal : destinataires d'un partage, e-mail et position d'un intervenant. */
export function journalText(entry, people = null) {
    if (entry.shareWith) return `${entry.label} avec ${shareRecipients(people)}`;
    const responder = entry.responder;
    if (!responder) return entry.label;
    const known = responder.id != null ? people?.[responder.id] : null;
    const who = responder.email || known?.email || known?.displayName || null;
    const where = responder.lat != null ? ` : ${responder.lat.toFixed(5)}, ${responder.lng.toFixed(5)}` : '';
    return `${entry.label}${who ? ` ${who}` : ''}${where}`;
}

/**
 * Journal de la session (vue relecture) : une ligne par événement reçu, quel
 * que soit son type, rejoué avec processEvent comme dans le direct. La vue
 * n'affiche que les lignes déjà atteintes par la lecture.
 * Chaque entrée : { t (ms), label, color, kind }.
 */
export function buildSessionJournal(events, initialSnapshot = {}) {
    const session = { responders: {}, victimRoute: [], ...JSON.parse(JSON.stringify(initialSnapshot || {})) };
    const journal = [];
    const describe = s => stripIcon(getAlertDetails(s.sessionType, s.trigger, s.shareType, {
        appType: s.appType, activityType: s.activityType, shortcutDetectionMode: s.shortcutDetectionMode,
        travelMode: s.selectedTravelMode || s.travelMode, trailName: s.destinationAddressName,
    }).message);
    // « … avec vous » est écrit pour la personne qui suit : dans le journal, on
    // nomme les destinataires (journalText), faute de mieux « une personne ».
    const shareEntry = (entry) => (/ avec vous$/.test(entry.label)
        ? { ...entry, label: entry.label.replace(/ avec vous$/, ''), shareWith: true }
        : entry);
    const sorted = [...(events || [])].sort((a, b) => getEventTimestamp(a) - getEventTimestamp(b));
    if (sorted.length) {
        const color = getSessionTypeColor(session.sessionType);
        // Cyan : session encore locale sur le téléphone, avant tout partage.
        const label = color === CYAN ? 'Début : suivi local sur le téléphone (avant partage)' : `Début : ${describe(session) || 'session'}`;
        journal.push(shareEntry({ t: getEventTimestamp(sorted[0]), label, color, kind: 'START' }));
    }
    for (const event of sorted) {
        const t = getEventTimestamp(event);
        const type = event.eventType;
        const data = readData(event);
        const before = {
            sessionType: session.sessionType, trigger: session.trigger,
            audio: asBoolean(session.audio), video: asBoolean(session.video),
            isStopped: asBoolean(session.isStopped), responders: new Set(Object.keys(session.responders || {})),
        };
        processEvent(event, session, true);
        const push = (label, color = GREY) => journal.push(shareEntry({ t, label, color, kind: type }));

        if (type === 'NEWPOSITION') {
            const position = unwrapEventData(data.value ?? data) || {};
            const lat = Number(position.latitude ?? position.lat);
            const lng = Number(position.longitude ?? position.lng);
            const accuracy = Number(position.accuracy);
            push(Number.isFinite(lat) && Number.isFinite(lng)
                ? `Position ${lat.toFixed(5)}, ${lng.toFixed(5)}${Number.isFinite(accuracy) && accuracy > 0 ? ` (±${Math.round(accuracy)} m)` : ''}`
                : 'Position');
        } else if (type === 'CONVERTSESSION') {
            push(describe(session) || 'Changement de mode', getSessionTypeColor(session.sessionType));
            if (asBoolean(session.video) && !before.video) push('Vidéo activée', RED);
            else if (asBoolean(session.audio) && !before.audio) push('Micro activé', RED);
        } else if (type === 'STATECHANGE') {
            const prop = data.property || data.propertyName;
            if (prop === 'sessionType') push(describe(session) || 'Changement de mode', getSessionTypeColor(session.sessionType));
            else if (prop === 'isStopped') push(asBoolean(session.isStopped) ? 'Arrêt détecté' : 'Reprise du déplacement', ORANGE);
            else if (prop === 'stopTimerValue') push(`Décompte d’arrêt : ${shortValue(data.value)}`, ORANGE);
            else if (prop === 'audio' || prop === 'audioEnabled') push(asBoolean(session.audio) ? 'Micro activé' : 'Micro coupé', RED);
            else if (prop === 'video' || prop === 'videoEnabled') push(asBoolean(session.video) ? 'Vidéo activée' : 'Vidéo coupée', RED);
            // Passages : couleur de l'itinéraire (vers le parcours) puis du parcours, comme l'application.
            else if (prop === 'trailApproach' && session.trailApproachName) push(`En route vers le parcours${session.trailApproachName ? ` « ${session.trailApproachName} »` : ''}`, PASSAGE_COLORS.itinerary)
            else if (prop === 'trailStarted') push(`Parcours démarré${session.trailStartedName ? ` « ${session.trailStartedName} »` : ''}`, PASSAGE_COLORS.trail)
            else if (prop === 'selectedTravelMode' || prop === 'travelMode') push(`${PROPERTY_LABELS[prop]} : ${shortValue(data.value)}`, routePassageColor(session.travelMode))
            else if (prop === 'sportPause') push(`Pause déclarée${session.sportPauseUntil ? ` jusqu’à ${formatHour(session.sportPauseUntil)}` : ''}`, ORANGE);
            else if (prop === 'sportResume') push('Fin de la pause', ORANGE);
            else if (prop === 'sportCheck') push(`« Tout va bien ? » demandé (${SPORT_CHECK_REASONS[session.sportCheck] || 'vérification'})`, ORANGE);
            else if (prop === 'sportCheckOk') push('« Tout va bien » confirmé', ORANGE);
            else push(`${PROPERTY_LABELS[prop] || prop || 'État'} : ${shortValue(data.value)}`);
        } else if (type === 'RESPONDER_UPDATE') {
            let info = unwrapEventData(data.value ?? data);
            if (typeof info === 'string') { try { info = JSON.parse(info); } catch (_) { info = {}; } }
            info = info && typeof info === 'object' ? info : {};
            const lat = Number(info.lat ?? info.latitude);
            const lng = Number(info.lng ?? info.longitude);
            const located = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
            const id = info.userId != null ? String(info.userId) : null;
            const joined = id !== null && !before.responders.has(id) && located;
            // L'hôte complète l'e-mail s'il le connaît (liste des témoins) : voir responder.
            journal.push({
                t, kind: type, color: CYAN,
                responder: { id, email: info.userEmail || null, lat: located ? lat : null, lng: located ? lng : null, status: info.status || null },
                label: located ? (joined ? 'Intervenant arrivé dans la session' : 'Intervenant') : 'Intervenant retiré de la carte',
            });
        } else if (type === 'END' || type === 'SESSION_END') {
            push('Fin de la session');
        } else {
            push(type ? `Événement ${type}` : 'Événement');
        }
    }
    return journal;
}

/** Passage en suivi causé par l'alerte (quelques secondes après) : l'alerte suffit. */
const ALERT_PASSAGE_MS = 10_000;
/** Événements d'un même changement (tracé, mode, nom) : ils arrivent en quelques secondes. */
const PASSAGE_SETTLE_MS = 5_000;
const PASSAGE_LABELS = { follow: 'Suivi', itinerary: 'Itinéraire', trail: 'Parcours' };

/** Passage affiché sur la carte : pas de tracé = suivi ; tracé de randonnée/course = parcours. */
function passageOf(session) {
    if (!(session.routePoints || []).length) return 'follow';
    return routePassageColor(session.travelMode) === PASSAGE_COLORS.trail ? 'trail' : 'itinerary';
}

/**
 * Repères de la timeline, comme l'application : « Début », puis dans l'ordre les états
 * (partage, alerte) et les passages (suivi, itinéraire, parcours), puis « Fin ».
 * Début et Fin portent la couleur du passage, sauf en alerte.
 * Chaque repère : { t (ms), label, color, edge: 'start' | 'end' | null }.
 */
export function buildTimelineMarkers(events, initialSnapshot = {}) {
    const sorted = [...(events || [])].sort((a, b) => getEventTimestamp(a) - getEventTimestamp(b));
    if (!sorted.length) return [];
    const session = { responders: {}, victimRoute: [], ...JSON.parse(JSON.stringify(initialSnapshot || {})) };
    const startT = getEventTimestamp(sorted[0]);
    let endT = getEventTimestamp(sorted[sorted.length - 1]);
    const initialPassage = passageOf(session);
    const initialState = getSessionTypeColor(session.sessionType);
    let passage = initialPassage;
    let state = initialState;
    const passages = [];
    const states = [];
    for (const event of sorted) {
        const t = getEventTimestamp(event);
        const type = event.eventType;
        if (type === 'END' || type === 'SESSION_END') { endT = t; break; }
        processEvent(event, session, true);
        const nextPassage = passageOf(session);
        if (nextPassage !== passage) passages.push({ t, kind: nextPassage });
        passage = nextPassage;
        const nextState = getSessionTypeColor(session.sessionType);
        if (nextState !== state) states.push({ t, color: nextState });
        state = nextState;
    }
    const endPassage = passage;
    const endState = state;

    // Un changement remplacé en quelques secondes n'en est pas un : on garde le dernier.
    const settled = passages.filter((p, i) => !(passages[i + 1] && passages[i + 1].t - p.t < PASSAGE_SETTLE_MS));
    // Le premier passage est porté par « Début ».
    let startPassage = initialPassage;
    while (settled.length && settled[0].t - startT <= PASSAGE_SETTLE_MS) startPassage = settled.shift().kind;
    const alertTimes = states.filter(s => s.color === RED).map(s => s.t);
    const passageMarkers = [];
    let previous = startPassage;
    for (const p of settled) {
        if (p.kind === previous) continue;
        previous = p.kind;
        if (alertTimes.some(a => p.t - a >= -1000 && p.t - a <= ALERT_PASSAGE_MS)) continue;
        passageMarkers.push({ t: p.t, label: PASSAGE_LABELS[p.kind], color: PASSAGE_COLORS[p.kind], edge: null });
    }
    // Seul le premier partage compte : ensuite, on est déjà en partage (retour après une alerte…).
    const firstShare = states.find(s => s.color === '#FFD60A');
    const stateMarkers = states
        .filter(s => s.color === RED || s === firstShare)
        .map(s => ({ t: s.t, label: s.color === RED ? 'Alerte' : 'Partage', color: s.color, edge: null }));

    const edgeColor = (st, kind) => (st === RED ? RED : PASSAGE_COLORS[kind]);
    return [
        { t: startT, label: 'Début', color: edgeColor(initialState, startPassage), edge: 'start' },
        ...[...stateMarkers, ...passageMarkers].sort((a, b) => a.t - b.t),
        { t: endT, label: 'Fin', color: edgeColor(endState, endPassage), edge: 'end' },
    ];
}
